import clsx from "clsx";
import React from "react";
import { useSettings } from "../../hooks/settings";

import styles from "./DebugPane.module.css";

export interface DebugPaneProps {
  className?: string;
  style?: React.CSSProperties;
  /** Show even when `settings.dev` is false. Variants normally inherit the gate. */
  forceVisible?: boolean;
  children?: React.ReactNode;
}

/**
 * Styled container for debug overlays. Hidden unless `settings.dev` is on (or `forceVisible`).
 * Variants compose this base and feed it the actual content (rows, tables, badges).
 */
export const DebugPane = ({ className, style, forceVisible, children }: DebugPaneProps) => {
  const [settings] = useSettings();
  if (!forceVisible && !settings.dev) return null;
  return (
    <div className={clsx(styles.container, className)} style={style}>
      {children}
    </div>
  );
};

export interface DebugRowProps {
  label?: React.ReactNode;
  value?: React.ReactNode;
  children?: React.ReactNode;
  style?: React.CSSProperties;
}

/** A single label/value line, aligned with siblings via the shared CSS class. */
export const DebugRow = ({ label, value, children, style }: DebugRowProps) => (
  <div className={styles.row} style={style}>
    {label !== undefined && <span className={styles.rowLabel}>{label}</span>}
    {value !== undefined && <span className={styles.rowValue}>{value}</span>}
    {children}
  </div>
);

export interface DebugDotProps {
  on?: boolean;
  title?: string;
  /** Override the "on" colour (default green). */
  color?: string;
}

export const DebugDot = ({ on, title, color = "#2ecc71" }: DebugDotProps) => (
  <span
    className={styles.dot}
    style={{ background: on ? color : "#444" }}
    title={title}
  />
);

export const DebugSection = ({ children, style }: { children: React.ReactNode; style?: React.CSSProperties }) => (
  <div className={styles.section} style={style}>{children}</div>
);
